/**
 * Demo page: /ui-elements/buttons (ghost button).
 * Spec 76 Phase B — CloseSummaryCard.
 *
 * Renders the inline card that appears in the parent conversation when an
 * editor panel is closed. The card is driven by a system message whose
 * content is `JSON.stringify({ contextPanelClosed: { type, id, summary } })`.
 *
 * `parseCloseSummary` is the companion parser — mirrors the defensive style
 * of `DisambiguationPills.parseAmbiguousMessage`.
 *
 * The "View →" link opens the panel again via `useContextPanel().openPanel`.
 */
import React from 'react';
import { FileText } from 'lucide-react';
import { Button } from '../ui/Button.js';
import { sentenceCase } from '../ui/FilterSelect.js';
import { getPanelType } from './registry.js';
import { useContextPanel } from './url-state.js';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CloseSummaryPayload {
  type: string;
  id: string;
  summary: string;
}

// ─── Parser ───────────────────────────────────────────────────────────────────

/**
 * Parse a system-message `content` string into a CloseSummaryPayload.
 *
 * Returns `null` when:
 * - content is not valid JSON
 * - the `contextPanelClosed` envelope is absent or not an object
 * - any of `type`, `id`, or `summary` is missing or not a string
 *
 * Extra/surplus keys in the payload object are silently ignored.
 */
export function parseCloseSummary(content: string): CloseSummaryPayload | null {
  try {
    const parsed = JSON.parse(content) as unknown;
    if (typeof parsed !== 'object' || parsed === null) return null;

    const root = parsed as Record<string, unknown>;
    const envelope = root.contextPanelClosed;
    if (typeof envelope !== 'object' || envelope === null) return null;

    const e = envelope as Record<string, unknown>;
    const type = typeof e.type === 'string' ? e.type : null;
    const id = typeof e.id === 'string' ? e.id : null;
    const summary = typeof e.summary === 'string' ? e.summary : null;

    if (!type || !id || summary === null) return null;

    return { type, id, summary };
  } catch {
    return null;
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

interface CloseSummaryCardProps extends CloseSummaryPayload {}

export const CloseSummaryCard: React.FC<CloseSummaryCardProps> = ({ type, id, summary }) => {
  const { openPanel } = useContextPanel();

  const definition = getPanelType(type);
  // Fall back to the raw type string if the type isn't (yet) registered.
  const typeLabel = sentenceCase(definition?.type ?? type);

  return (
    <div className="bg-surface-alt border rounded-lg p-3 text-sm space-y-2">
      {/* Heading row */}
      <div className="flex items-center gap-1.5">
        <FileText size={14} className="shrink-0 text-muted" />
        <span className="text-xs text-muted font-medium">{typeLabel}</span>
      </div>

      {/* Summary text — plain whitespace-pre-wrap, no markdown lib */}
      <p className="text-body whitespace-pre-wrap">{summary}</p>

      {/* "View →" action */}
      <Button variant="ghost" size="sm" onClick={() => openPanel({ type, id })} aria-label={`View ${typeLabel}`}>
        View →
      </Button>
    </div>
  );
};
